import {Component, computed, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {AsyncPipe, NgTemplateOutlet} from "@angular/common";
import {RouterLink} from "@angular/router";
import {MatListModule} from "@angular/material/list";
import {MatIcon} from "@angular/material/icon";
import {MatBadge} from "@angular/material/badge";
import {MatExpansionModule} from "@angular/material/expansion";
import {MatLine} from "@angular/material/core";
import {Observable, Subject, takeUntil} from "rxjs";
import {NavScopeService} from "../../../services/nav-scope.service";
import {GameService} from "../../../services/game.service";
import {ActiveUserService} from "../../../services/active-user.service";
import {PermissionsService} from "../../../services/permissions.service";
import {SupportRequestService} from "../../../services/support-request.service";
import {OidcService} from "../../../services/oidc.service";
import {AppConfigService} from "../../../services/app-config.service";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {PermissionTypes} from "../../../enum/PermissionTypes";

/**
 * The single, config-driven admin side-nav (Release 1.19 IA pass, Stage 2).
 *
 * Replaces the two hand-written, drifted templates (cross-game-admin-side-nav +
 * game-admin-side-nav). It renders ADMIN_NAV grouped into task domains, resolving
 * visibility, routes and legacy test-ids per the current scope via NavScopeService.
 */
@Component({
  selector: 'app-admin-nav',
  standalone: true,
  imports: [
    AsyncPipe,
    NgTemplateOutlet,
    RouterLink,
    MatListModule,
    MatIcon,
    MatBadge,
    MatExpansionModule,
    MatLine,
  ],
  templateUrl: './admin-nav.component.html',
  styleUrl: '../side-nav.component.scss',
})
export class AdminNavComponent implements OnInit, OnDestroy {
  private navScope = inject(NavScopeService);
  private gameService = inject(GameService);
  private activeUserService = inject(ActiveUserService);
  private permissions = inject(PermissionsService);
  private supportRequestService = inject(SupportRequestService);
  private oidcService = inject(OidcService);
  private appConfigService = inject(AppConfigService);

  private destroy$ = new Subject<void>();

  public activeGame$: Observable<IGameQueryResult | undefined> = this.gameService.selectActiveGame$();
  public hangfireLink = '';
  public assignedSupportRequestsCount = signal<number>(0);

  // Derived as a signal so visibility re-evaluates whenever the active game or the signed-in admin's permissions change.
  private activeGameId = toSignal(this.gameService.selectActiveGameId(), {initialValue: ''});
  private drawnManually = toSignal(this.gameService.selectActiveGameDrawnManually(), {initialValue: false});
  private activeUser = toSignal(this.activeUserService.selectActiveUser());

  private groups = computed(() => {
    this.activeUser(); // dependency: rebuild when the user (and thus their permissions) loads/changes
    const gameId = this.activeGameId();
    return this.navScope.buildVisibleGroups(this.navScope.scopeForGameId(gameId), gameId, this.drawnManually());
  });

  public domainGroups = computed(() => this.groups().filter(group => !group.isFooter));
  public footerGroups = computed(() => this.groups().filter(group => group.isFooter));

  ngOnInit(): void {
    this.appConfigService.appConfig$
      .pipe(takeUntil(this.destroy$))
      .subscribe(config => this.hangfireLink = config.commonUrl + '/hangfire');

    this.refreshAssignedRequestsCount();

    // Refresh the badge when assignments change or the active game switches.
    this.supportRequestService.assignmentChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshAssignedRequestsCount());

    this.gameService.selectActiveGameId()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshAssignedRequestsCount());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onLogoutClick(): void {
    this.oidcService.logout();
  }

  private refreshAssignedRequestsCount(): void {
    const userId = this.activeUserService.activeUser()?.Id;
    if (!userId) return;

    if (!this.permissions.userHasPermission(PermissionTypes.ViewSupportRequests)) {
      return;
    }

    // Game scope filters to the active game; global scope counts all assignments.
    const gameId = this.gameService.activeGame()?.Id || null;

    this.supportRequestService.fetchMyAssignedRequests(userId, gameId).subscribe({
      next: (requests) => this.assignedSupportRequestsCount.set(requests.filter(r => !r.Resolved).length),
      error: () => { /* silent background refresh */ },
    });
  }
}
